import { useState } from 'react';
import useFormValidation from '../../../../../hooks/useFormValidation';
import useRadiologicalEquipmentFieldsConfig from './useRadiologicalEquipmentFieldsConfig';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../../hooks/useTranslate';

export interface IRadiologicalEquipmentForm {
    brand: string;
    model: string;
    equipmentType: any;
    substance: any;
    characteristics: any;
    radiationType: any;
    activityCURIE: string;
    radioisotopeOfTheSource: string;
    sourceType: any;
    equipmentPowerKVP: string;
    equipmentPowerMAP: string;
    equipmentPowerMEV: string;
    imageProcessing: any;
    processingMethod: any;
    observations: string;
}

const INITIAL_VALUES: IRadiologicalEquipmentForm = {
    brand: '',
    model: '',
    equipmentType: null,
    substance: null,
    characteristics: null,
    radiationType: null,
    activityCURIE: '',
    radioisotopeOfTheSource: '',
    sourceType: null,
    equipmentPowerKVP: '',
    equipmentPowerMAP: '',
    equipmentPowerMEV: '',
    imageProcessing: null,
    processingMethod: null,
    observations: ''
};

export interface RadiologicalEquipmentFormProps {
    selectedCarcinogenicReportId: number | null;
    selectedRadiologicalEquipment: any;
    isEditing: boolean;
    handleCloseDrawer: any;
}

interface GetInitialData {
    selectedRadiologicalEquipment: any;
    isEditing: boolean;
} 
const getInitialData = ({ selectedRadiologicalEquipment, isEditing }: GetInitialData) => {
    if (!isEditing) return INITIAL_VALUES;
    return {
        brand: selectedRadiologicalEquipment?.marca || '',
        model: selectedRadiologicalEquipment?.modelo || '',
        equipmentType: selectedRadiologicalEquipment?.idTipoEquipo || null,
        substance: selectedRadiologicalEquipment?.sustancia?.id || null,
        characteristics: selectedRadiologicalEquipment?.idCaracteristica || null,
        radiationType: selectedRadiologicalEquipment?.idTipoRadiacion || null,
        activityCURIE: selectedRadiologicalEquipment?.actividad || '',
        radioisotopeOfTheSource: selectedRadiologicalEquipment?.radioisotopoFuente || '',
        sourceType: selectedRadiologicalEquipment?.idTipoFuente || null,
        equipmentPowerKVP: selectedRadiologicalEquipment?.potenciaKVP || '',
        equipmentPowerMAP: selectedRadiologicalEquipment?.potenciaMAP || '',
        equipmentPowerMEV: selectedRadiologicalEquipment?.potenciaMEV || '',
        imageProcessing: selectedRadiologicalEquipment?.procesamientoImagenes || null,
        processingMethod: selectedRadiologicalEquipment?.idMetodoProcesamiento || null,
        observations: selectedRadiologicalEquipment?.observaciones || ''
    };
};

export const useRadiologicalEquipmentForm = ({
    selectedCarcinogenicReportId,
    selectedRadiologicalEquipment,
    isEditing,
    handleCloseDrawer
}: RadiologicalEquipmentFormProps) => {
    const initialValues = getInitialData({ selectedRadiologicalEquipment, isEditing });

    const [formData, setFormData] = useState<IRadiologicalEquipmentForm>(initialValues);

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.radiologicalEquipment'
    });

    const { translate } = useTranslate(t);

    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();

    const { radiologicalEquipmentFieldsConfig } = useRadiologicalEquipmentFieldsConfig({
        t,
        formData,
        handleBlurValidation
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };


    const handleSubmit = async ({ onValidationSuccess }: any) => {
        if (!validateForm(formData, radiologicalEquipmentFieldsConfig)) return;
        onValidationSuccess()
    };

    return {
        formData,
        errors,
        selectedCarcinogenicReportId,
        handleChange,
        handleBlurValidation,
        handleSubmit,
        translate,
        validateForm,
        radiologicalEquipmentFieldsConfig,
        handleCloseDrawer
    };
};

export default useRadiologicalEquipmentForm;